import React from "react";
import { CButton, CSpinner } from "@coreui/react";
import type { ApiJob } from "@tradingdroid/frontends-types";

import { useJobPauseResume } from "@src/hooks/useJobPauseResume";
import { canPauseJob, canResumeJob } from "@src/utils/jobPauseResume";
import "./JobPauseResumeButton.scss";

interface Props {
  job: ApiJob;
  /** Receives the refreshed job after an action, for a page that holds its own copy. */
  onUpdated?: (job: ApiJob) => void;
  /** Icon-only ghost button for table rows. */
  compact?: boolean;
}

/** Pause for an optimization still in flight, Resume for a paused or cancelled one, else nothing. */
export const JobPauseResumeButton: React.FC<Props> = ({ job, onUpdated, compact = false }) => {
  const { pauseJob, resumeJob, busyJobId } = useJobPauseResume(onUpdated);

  const pausable = canPauseJob(job);
  if (!pausable && !canResumeJob(job)) return null;

  const label = pausable ? "Pause" : "Resume";
  const className = `JobPauseResumeButton ${pausable ? "JobPauseButton" : "JobResumeButton"}${
    compact ? " JobPauseResumeButtonCompact" : ""
  }`;

  return (
    <CButton
      color={pausable ? "warning" : "success"}
      variant={compact ? "ghost" : "outline"}
      size="sm"
      className={className}
      disabled={busyJobId === job.id}
      onClick={() => (pausable ? pauseJob(job) : resumeJob(job))}
      title={`${label} optimization`}
    >
      {busyJobId === job.id ? <CSpinner size="sm" /> : pausable ? "⏸️" : "▶️"}
      {!compact && ` ${label}`}
    </CButton>
  );
};
